import InqueritoCard from "../../Components/Graduado/InqueritoCard";
import {
  checkBeforeFillEmpregabilidadeForm,
  getInqueritos,
} from "../../API/getters";
import { useEffect, useState } from "react";
import EmployerSurveyCard from "../../Components/Empregador/EmployerSurveyCard";
import {
  Loader2,
  Briefcase,
  ClipboardCheck,
  Building2,
  RefreshCw,
} from "lucide-react";

export default function EmpregabilidadePage() {
  const [graduateStatusForm, setGraduateStatusForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [inqueritosData, setInqueritosData] = useState([]);
  const [studyOutputIndex, setStudyOutputIndex] = useState(0);

  const studyOutputs = [
    "% Graduados empregados nos últimos 3 anos",
    "% Graduados que empreenderam no mesmo período",
    "% Graduados com emprego na área de formação",
    "% Graduados que conseguiram emprego no 1.º ano após a graduação",
  ];

  async function fetchData() {
    try {
      setLoading(true);
      setError(null);
      const response = await checkBeforeFillEmpregabilidadeForm();
      setGraduateStatusForm(response || {});

      const res = await getInqueritos();
      const activeInqueritos = Array.isArray(res)
        ? res.filter((inquerito) => inquerito.active === true)
        : [];
      setInqueritosData(activeInqueritos);
    } catch (err) {
      console.error("Erro ao buscar dados:", err);
      setError(err?.message || "Erro desconhecido");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const interval = window.setInterval(() => {
      setStudyOutputIndex((i) => (i + 1) % studyOutputs.length);
    }, 4500);
    return () => window.clearInterval(interval);
  }, [studyOutputs.length]);

  const isEmpty = !graduateStatusForm || !graduateStatusForm.year;

  // Loading state
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <Loader2 className="w-8 h-8 text-emerald-500 animate-spin mb-3" />
        <span className="text-gray-400 text-sm">
          A carregar informações do seu inquérito...
        </span>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <div className="text-red-500 text-sm mb-3">{error}</div>
        <button
          onClick={fetchData}
          className="inline-flex items-center gap-2 text-emerald-600 text-sm hover:text-emerald-700 transition-colors"
        >
          <RefreshCw size={14} />
          Tentar novamente
        </button>
      </div>
    );
  }

  return (
    <div className="w-full py-6">
      <div className="mx-auto w-full max-w-[1400px]">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12 items-stretch">
        <div className="lg:col-span-8 space-y-8 h-full flex flex-col">
          {/* Inquérito do Graduado Section */}
          {!isEmpty && <InqueritoCard data={graduateStatusForm} />}

          {/* Inquéritos para Empregadores Section */}
          <section className="rounded-2xl border border-emerald-100 bg-gradient-to-b from-white to-emerald-50/40 p-6 shadow-sm">
            <div className="mb-6 pb-2 border-b border-emerald-100">
              <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                <Briefcase className="w-5 h-5 text-emerald-600" />
                Inquéritos disponíveis
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Participe dos inquéritos disponíveis e ajude a melhorar a
                empregabilidade
              </p>
            </div>

            {inqueritosData.length > 0 ? (
              <div className="space-y-4">
                {inqueritosData.map((inquerito) => (
                  <EmployerSurveyCard
                    key={inquerito.id}
                    id={inquerito.id}
                    title={inquerito.title}
                    description={inquerito.description}
                    code={inquerito.code}
                    targetGroup={inquerito.target_group}
                    active={inquerito.active}
                  />
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gray-50 mb-4">
                  <Building2 className="h-8 w-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-light text-gray-700 mb-1">
                  Nenhum inquérito disponível
                </h3>
                <p className="text-sm text-gray-400">
                  Volte mais tarde para ver novos inquéritos
                </p>
              </div>
            )}
          </section>
        </div>

        <aside className="lg:col-span-4 h-full" >
          <div className="rounded-2xl border bg-green-100 border-emerald-100 bg-white p-6 shadow-sm h-full flex flex-col">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-50 text-emerald-700">
                <ClipboardCheck className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <div className="text-base font-semibold font-[Robboto] md:text-[25px] text-gray-900">
                  SAÍDAS DO ESTUDO
                </div>
              </div>
            </div>

            <div className="mt-2 h-px w-full bg-gray-100" />
            <div className="mt-4 flex-1">
              <div className="mt-3 overflow-hidden rounded-2xl border border-emerald-100 bg-gradient-to-b from-white to-emerald-50/40 shadow-sm">
                <div className="relative p-20">
                  <div className="flex items-start gap-3" aria-live="polite">
                    <div className="mt-2 h-2.5 w-2.5 shrink-0 rounded-full bg-emerald-600" />
                    <div className="text-base font-semibold  font-[Robboto] leading-6 text-gray-1000 md:text-[25px]">
                      {studyOutputs[studyOutputIndex]}
                    </div>
                  </div>

                  <div className="mt-4 flex items-center justify-center gap-1.5">
                    {studyOutputs.map((_, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => setStudyOutputIndex(idx)}
                        className={`h-2.5 w-2.5 rounded-full transition ${
                          idx === studyOutputIndex
                            ? "bg-emerald-600"
                            : "bg-emerald-100 hover:bg-emerald-200"
                        }`}
                        aria-label={`Ir para item ${idx + 1}`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </aside>
        </div>
      </div>
    </div>
  );
}
